import { useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { Bell, ShieldCheck, ShieldAlert, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, useApi } from "../lib/api";
import { fmtUsd, fmtNum, shortAddr, timeAgo } from "../lib/format";
import { CopyBtn, AddrLink, TokenLogo, WatchStar, DataBadge, SectionHead, Skel, Empty } from "../components/common";
import BubblePack from "../components/BubblePack";
import usePageMeta from "../lib/usePageMeta";
import { BLOCKSCOUT_URL } from "../lib/wallet";

const Info = ({ label, value, cls = "" }) => (
  <div className="px-3 py-2 border-b border-r border-[#1a1a1a]">
    <div className="text-[10px] uppercase tracking-wider text-[#6B7280]">{label}</div>
    <div className={`num text-[13px] mt-0.5 ${cls}`}>{value}</div>
  </div>
);

function Transfers({ addr, token }) {
  const { data, loading } = useApi(`/tokens/${addr}/transfers`, { refreshInterval: 30000 });
  if (loading) return <Skel rows={10} h={30} />;
  if (!data?.items?.length) return <Empty msg="No recent transfers" />;
  return (
    <div className="t-card overflow-x-auto">
      <table className="w-full t-table" data-testid="transfers-table">
        <thead>
          <tr className="border-b border-[#262626] text-left">
            <th>Time</th><th>Method</th><th>From</th><th>To</th><th className="text-right">Amount</th><th className="text-right">USD</th><th>Tx</th>
          </tr>
        </thead>
        <tbody>
          {data.items.map((t, i) => {
            const amt = t.total?.value ? parseInt(t.total.value) / 10 ** (parseInt(t.total.decimals ?? token.decimals) || 18) : null;
            const usd = amt != null && token.price != null ? amt * token.price : null;
            return (
              <tr key={i} data-testid="transfer-row">
                <td className="num text-[#6B7280]">{t.timestamp ? timeAgo(new Date(t.timestamp).getTime() / 1000) : "—"}</td>
                <td className="text-xs text-[#9CA3AF]">{t.method || t.type || "transfer"}</td>
                <td><AddrLink addr={t.from?.hash} label={t.from?.name || undefined} /></td>
                <td><AddrLink addr={t.to?.hash} label={t.to?.name || undefined} /></td>
                <td className="text-right num text-[#9CA3AF]">{amt != null ? fmtNum(amt) : "—"}</td>
                <td className={`text-right num ${usd != null && usd > 10000 ? "text-[#60A5FA]" : ""}`}>{usd != null ? fmtUsd(usd) : "—"}</td>
                <td><Link to={`/explorer/tx/${t.transaction_hash}`} className="num text-xs text-[#10B981] hover:underline" onClick={(e) => e.stopPropagation()}>{shortAddr(t.transaction_hash, 4)}</Link></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function TransferActivity({ addr }) {
  const { data } = useApi(`/tokens/${addr}/transfers`);
  const buckets = useMemo(() => {
    if (!data?.items?.length) return [];
    const times = data.items.filter((t) => t.timestamp).map((t) => new Date(t.timestamp).getTime());
    if (!times.length) return [];
    const spanMin = (Math.max(...times) - Math.min(...times)) / 60000;
    const binMs = spanMin < 2 ? 10000 : spanMin < 30 ? 60000 : spanMin < 180 ? 300000 : 3600000;
    const map = {};
    times.forEach((ms) => {
      const d = new Date(Math.floor(ms / binMs) * binMs);
      const key = binMs < 60000
        ? `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}:${String(d.getUTCSeconds()).padStart(2, "0")}`
        : `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`;
      map[key] = (map[key] || 0) + 1;
    });
    return Object.entries(map).map(([hour, count]) => ({ hour, count })).sort((a, b) => a.hour.localeCompare(b.hour));
  }, [data]);
  if (!buckets.length) return null;
  return (
    <div className="t-card">
      <div className="px-3 py-2 border-b border-[#262626] flex items-center justify-between">
        <span className="text-xs uppercase text-[#6B7280]">Transfer activity — last {data.items.length} transfers (UTC)</span>
        <DataBadge live />
      </div>
      <div className="h-40 p-2" data-testid="transfer-activity-chart">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={buckets}>
            <XAxis dataKey="hour" stroke="#3F3F46" fontSize={10} tickLine={false} />
            <YAxis stroke="#3F3F46" fontSize={10} tickLine={false} width={30} allowDecimals={false} />
            <Tooltip contentStyle={{ background: "#121212", border: "1px solid #262626", borderRadius: 2, fontSize: 12 }} formatter={(v) => [v, "Transfers"]} />
            <Bar dataKey="count" fill="#059669" radius={[2, 2, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function Holders({ addr, token }) {
  const { data, loading } = useApi(`/tokens/${addr}/holders`);
  if (loading) return <Skel rows={10} h={30} />;
  if (!data?.items?.length) return <Empty msg="Holder data unavailable" />;
  const supplyRaw = token.total_supply != null ? token.total_supply * 10 ** token.decimals : null;
  const rows = data.items.map((h, i) => {
    const bal = parseInt(h.value || "0") / 10 ** token.decimals;
    const pct = supplyRaw ? (parseInt(h.value || "0") / supplyRaw) * 100 : null;
    return { rank: i + 1, addr: h.address?.hash, name: h.address?.name, isContract: h.address?.is_contract, bal, pct };
  });
  const top10 = rows.slice(0, 10).reduce((a, r) => a + (r.pct || 0), 0);
  const dist = [
    { name: "Top 10 holders", value: top10, color: "#EF4444" },
    { name: "Other holders", value: Math.max(0, 100 - top10), color: "#059669" },
  ];
  return (
    <div className="space-y-3" data-testid="holders-section">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        <div className="t-card p-2.5"><div className="text-[10px] uppercase text-[#6B7280]">Total Holders</div><div className="num text-sm mt-0.5">{fmtNum(token.holders)}</div></div>
        <div className="t-card p-2.5"><div className="text-[10px] uppercase text-[#6B7280]">Top-10 Concentration</div><div className={`num text-sm mt-0.5 ${top10 > 50 ? "text-[#EF4444]" : "text-[#10B981]"}`}>{top10.toFixed(2)}%</div></div>
        <div className="t-card p-2.5"><div className="text-[10px] uppercase text-[#6B7280]">Total Transfers</div><div className="num text-sm mt-0.5">{fmtNum(token.transfers_count)}</div></div>
      </div>
      <div className="grid md:grid-cols-[220px_1fr] gap-3">
        <div className="t-card p-2 h-52" data-testid="holder-distribution-chart">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={dist} dataKey="value" innerRadius={42} outerRadius={70} paddingAngle={2} stroke="none">
                {dist.map((d) => <Cell key={d.name} fill={d.color} />)}
              </Pie>
              <Tooltip contentStyle={{ background: "#121212", border: "1px solid #262626", borderRadius: 2, fontSize: 12 }} formatter={(v, n) => [`${v.toFixed(2)}%`, n]} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="t-card overflow-x-auto">
          <table className="w-full t-table" data-testid="top-holders-table">
            <thead><tr className="text-left border-b border-[#262626]"><th>#</th><th>Wallet</th><th>Label</th><th className="text-right">Balance</th><th className="text-right">% Supply</th><th className="text-right">USD Value</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.rank}>
                  <td className="num text-[#6B7280]">{r.rank}</td>
                  <td><AddrLink addr={r.addr} /></td>
                  <td className="text-xs text-[#9CA3AF]">{r.name || (r.isContract ? "Contract" : "—")}</td>
                  <td className="text-right num text-[#9CA3AF]">{fmtNum(r.bal)}</td>
                  <td className={`text-right num ${r.pct > 10 ? "text-[#F59E0B]" : ""}`}>{r.pct != null ? `${r.pct.toFixed(2)}%` : "—"}</td>
                  <td className="text-right num text-[#9CA3AF]">{token.price != null ? fmtUsd(r.bal * token.price) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Contract({ addr, token }) {
  const { data, loading } = useApi(`/explorer/address/${addr}`);
  const { data: holders } = useApi(`/tokens/${addr}/holders`);
  if (loading) return <Skel rows={6} h={32} />;
  const info = data?.info || {};
  const supplyRaw = token.total_supply != null ? token.total_supply * 10 ** token.decimals : null;
  const top10 = holders?.items?.slice(0, 10).reduce((a, h) => a + (supplyRaw ? parseInt(h.value || "0") / supplyRaw * 100 : 0), 0);
  const checks = [
    { name: "Contract verification", ok: !!info.is_verified, detail: info.is_verified ? "Source code is verified on Blockscout and publicly readable." : "Source code is NOT verified on Blockscout. Behavior cannot be audited from source." },
    { name: "Token reputation", ok: token.reputation === "ok", detail: token.reputation === "ok" ? "Blockscout reputation flag: ok." : `Blockscout reputation flag: ${token.reputation || "unknown"}.` },
    { name: "Holder concentration", ok: (top10 ?? 0) < 50, detail: top10 != null ? `Top 10 holders control ${top10.toFixed(2)}% of supply.` : "Holder concentration unavailable." },
    { name: "Contract type", ok: true, detail: `${token.type} token contract${info.is_contract ? "" : " (address not flagged as contract)"}.` },
  ];
  return (
    <div className="space-y-3" data-testid="contract-section">
      <div className="t-card">
        {[["Contract", addr], ["Creator", info.creator_address_hash || "—"], ["Creation Tx", info.creation_transaction_hash || info.creation_tx_hash || "—"],
          ["ETH Balance", `${(parseInt(info.coin_balance || "0") / 1e18).toFixed(6)} ETH`], ["Verified", info.is_verified ? "Yes" : "No"]].map(([l, v]) => (
          <div key={l} className="flex flex-col sm:flex-row px-3 py-2.5 border-b border-[#1a1a1a] last:border-0 text-[13px]">
            <span className="w-40 text-[#6B7280] shrink-0">{l}</span>
            <span className="num break-all">
              {String(v).startsWith("0x") && String(v).length === 66 ? <Link to={`/explorer/tx/${v}`} className="text-[#10B981] hover:underline">{v}</Link>
                : String(v).startsWith("0x") && String(v).length === 42 && l !== "Contract" ? <Link to={`/wallet/${v}`} className="text-[#10B981] hover:underline">{v}</Link> : v}
              {String(v).startsWith("0x") && <CopyBtn text={String(v)} className="ml-1" />}
            </span>
          </div>
        ))}
      </div>
      <div className="grid md:grid-cols-2 gap-2">
        {checks.map((c) => (
          <div key={c.name} className={`t-card p-3 flex gap-2.5 ${!c.ok ? "border-[#EF444430]" : ""}`} data-testid={`security-check-${c.name.toLowerCase().replace(/ /g, "-")}`}>
            {c.ok ? <ShieldCheck size={16} className="text-[#10B981] shrink-0 mt-0.5" /> : <ShieldAlert size={16} className="text-[#F59E0B] shrink-0 mt-0.5" />}
            <div>
              <div className="text-[13px] font-medium">{c.name}</div>
              <div className="text-xs text-[#9CA3AF] mt-0.5">{c.detail}</div>
            </div>
          </div>
        ))}
      </div>
      <a href={`${BLOCKSCOUT_URL}/token/${addr}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-[#9CA3AF] hover:text-[#10B981]">
        <ExternalLink size={12} /> View full contract details on Blockscout
      </a>
    </div>
  );
}

function HolderBubbles({ addr, token }) {
  const { data, loading } = useApi(`/tokens/${addr}/holders`);
  const nav = useNavigate();
  if (loading) return <Skel rows={8} h={40} />;
  if (!data?.items?.length) return <Empty msg="Holder data unavailable" />;
  const supplyRaw = token.total_supply != null ? token.total_supply * 10 ** token.decimals : null;
  const items = data.items.map((h, i) => {
    const bal = parseInt(h.value || "0") / 10 ** token.decimals;
    const pct = supplyRaw ? (parseInt(h.value || "0") / supplyRaw) * 100 : null;
    const isContract = h.address?.is_contract;
    return {
      id: h.address?.hash,
      label: h.address?.name ? h.address.name.slice(0, 14) : shortAddr(h.address?.hash, 3),
      tooltip: h.address?.name || h.address?.hash,
      sub: `${pct != null ? pct.toFixed(2) + "% supply · " : ""}${fmtNum(bal)} ${token.symbol}`,
      value: bal,
      color: isContract ? "#60A5FA" : i < 10 ? "#EF4444" : "#10B981",
    };
  });
  return (
    <div className="space-y-2" data-testid="holder-bubblemap">
      <div className="flex flex-wrap gap-3 text-[11px] text-[#6B7280]">
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full border" style={{ borderColor: "#EF4444", background: "#EF444430" }} /> Top-10 holder</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full border" style={{ borderColor: "#60A5FA", background: "#60A5FA30" }} /> Contract (pool / exchange / vault)</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full border" style={{ borderColor: "#10B981", background: "#10B98130" }} /> Other wallet</span>
        <span className="ml-auto">Bubble size = live token balance · click a bubble to open the wallet</span>
      </div>
      <div className="t-card p-2">
        <BubblePack items={items} height={520} onClick={(d) => d.id && nav(`/wallet/${d.id}`)} valueLabel={(d) => d.sub.split(" · ")[0]} />
      </div>
    </div>
  );
}

const TABS = ["Transfers", "Holders", "Bubble Map", "Contract & Security"];

export default function TokenDetail() {
  const { address } = useParams();
  const { data: t, error } = useApi(`/tokens/${address}`);
  usePageMeta(t ? `${t.symbol} ${t.price != null ? "$" + t.price : ""} — ${t.name}` : "Token", t ? `Live ${t.name} (${t.symbol}) analytics on Robinhood Chain: price, volume, holders, transfers and contract security.` : undefined);
  const [tab, setTab] = useState(TABS[0]);

  if (error) return <Empty msg="Token not found on Robinhood Chain" />;
  if (!t) return <Skel rows={10} h={44} />;

  const createAlert = async () => {
    const value = prompt(`Alert when ${t.symbol} price goes above ($):`, t.price ? (t.price * 1.1).toFixed(2) : "");
    if (!value) return;
    await api.post("/alerts", { target_type: "token", target_id: t.address, target_label: t.symbol, condition: "price_above", value: parseFloat(value) });
    toast.success(`Alert created for ${t.symbol} above $${value}`);
  };

  return (
    <div className="space-y-4">
      <div className="t-card p-4" data-testid="token-header">
        <div className="flex flex-wrap items-center gap-3">
          <TokenLogo symbol={t.symbol} icon={t.icon_url} size={40} />
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-heading font-bold text-xl">{t.name}</h1>
              <span className="num text-sm text-[#6B7280]">{t.symbol}</span>
              {t.is_stock && <span className="text-[10px] uppercase text-[#F59E0B] border border-[#F59E0B40] px-1.5 rounded-sm">Tokenized Stock</span>}
              <DataBadge live />
            </div>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="num text-xs text-[#6B7280]" data-testid="token-address">{shortAddr(t.address, 8)}</span>
              <CopyBtn text={t.address} />
              <span className="text-[10px] text-[#6B7280] border border-[#262626] px-1.5 rounded-sm">{t.type}</span>
            </div>
          </div>
          {t.price != null && (
            <div className="md:ml-6">
              <div className="num text-2xl font-semibold" data-testid="token-price">{fmtUsd(t.price)}</div>
              <div className="text-[10px] uppercase text-[#6B7280]">Onchain price</div>
            </div>
          )}
          <div className="ml-auto flex items-center gap-2">
            <WatchStar type="token" id={t.address} label={t.name} sub={t.symbol} />
            <button data-testid="create-alert-button" onClick={createAlert} className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 border border-[#262626] rounded-sm hover:border-[#3F3F46] transition-colors"><Bell size={13} /> Alert</button>
          </div>
        </div>
      </div>

      <div className="t-card grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6" data-testid="market-info-cards">
        <Info label="Price" value={t.price != null ? fmtUsd(t.price) : "—"} />
        <Info label="Volume 24h" value={t.volume_24h ? fmtUsd(t.volume_24h) : "—"} />
        <Info label="Market Cap" value={t.market_cap != null ? fmtUsd(t.market_cap) : "—"} />
        <Info label="Holders" value={fmtNum(t.holders)} />
        <Info label="Total Supply" value={t.total_supply != null ? fmtNum(t.total_supply) : "—"} />
        <Info label="Transfers" value={fmtNum(t.transfers_count)} />
      </div>

      <TransferActivity addr={t.address} />

      <div className="flex gap-px border-b border-[#262626]">
        {TABS.map((tb) => (
          <button key={tb} data-testid={`tab-${tb.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={() => setTab(tb)}
            className={`text-[13px] px-3.5 py-2 border-b-2 -mb-px transition-colors ${tab === tb ? "border-[#059669] text-[#F3F4F6]" : "border-transparent text-[#6B7280] hover:text-[#9CA3AF]"}`}>{tb}</button>
        ))}
      </div>

      {tab === "Transfers" && <Transfers addr={t.address} token={t} />}
      {tab === "Holders" && <Holders addr={t.address} token={t} />}
      {tab === "Bubble Map" && <HolderBubbles addr={t.address} token={t} />}
      {tab === "Contract & Security" && <Contract addr={t.address} token={t} />}
    </div>
  );
}
